/**
 * Firestore write-side helpers.
 *
 * Firestore rejects any document containing an `undefined` field value
 * — including deeply nested ones in arrays-of-objects. TypeScript's
 * `Partial<T>` and optional-field types do nothing to prevent this:
 * an object literal `{ foo: undefined }` happily type-checks and then
 * fails at runtime with "Unsupported field value: undefined".
 *
 * The common offender is data sourced from another doc / API where
 * optional fields landed as explicit `undefined` (e.g. AI matcher
 * output, snapshots that pass through TS-unaware code, etc.).
 *
 * Use `stripUndefinedDeep()` as the last step before every write that
 * carries nested objects / arrays. Cheap O(n) over the doc's value
 * tree; a 1KB write costs microseconds.
 */

/**
 * Recursively drop every property whose value is `undefined`. Arrays
 * recurse element-wise; primitives + null pass through. Returns a
 * NEW value — input is untouched. Safe for any shape Firestore
 * accepts (objects, arrays, strings, numbers, booleans, null,
 * Timestamps, FieldValues).
 *
 * Note: this preserves `null` (which Firestore accepts and which can
 * carry distinct meaning from "field absent"). Only `undefined` is
 * stripped.
 */
export function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefinedDeep(v)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    // Pass FieldValue sentinels (serverTimestamp, arrayUnion, etc.)
    // and Firestore Timestamps through untouched — they expose
    // internal shapes we shouldn't recurse into.
    const proto = Object.getPrototypeOf(value);
    const isPlainObject = proto === Object.prototype || proto === null;
    if (!isPlainObject) return value;
    const cleaned: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      cleaned[k] = stripUndefinedDeep(v);
    }
    return cleaned as T;
  }
  return value;
}
